import type { ReactNode } from "react";
import {
  BarChart3,
  BookOpen,
  ClipboardCheck,
  Database,
  FileQuestion,
  Layers,
  MessagesSquare,
  Phone,
  Search,
  Shield,
  Sparkles,
  UserRound,
  Users,
} from "lucide-react";
import type { NavKey } from "./router";
import type { UserOut } from "./api";

// Sidebar destinations only — excludes sub-views (`agent-profile`,
// `call-detail`) that are reachable from a sidebar page but never
// appear in the sidebar themselves.
export type SidebarNavKey = Exclude<
  NavKey,
  "agent-profile" | "call-detail" | "assessment-edit" | "assessment-view"
>;

export type NavUsage = {
  summary: string;
  howToUse: readonly string[];
};

export type NavSection = "top" | "bottom";

// T5 — human-readable titles for the two sidebar sections. The sidebar
// itself separates them with a plain divider; the How to Use reference
// shows these labels in its table of contents and card-column group
// headings. Keyed by `NavSection` so a new section can't ship unnamed.
export const NAV_SECTION_LABELS: Record<NavSection, string> = {
  top: "Everyday Tools",
  bottom: "Setup & Admin",
};

export type NavItem = {
  key: SidebarNavKey;
  label: string;
  icon: ReactNode;
  section: NavSection;
  isVisible: (user: UserOut) => boolean;
  usage: NavUsage;
};

const showAll = (_user: UserOut) => true;
const showDialpad = (user: UserOut) => user.role !== "manager";
// Wave 9 — the Data view (heavy sync/data tools relocated out of
// Dialpad) is super-admin only. This nav gate is cosmetic — every
// endpoint the view calls is role-gated server-side (require_role).
const showData = (user: UserOut) => user.role === "supadmin";
const showCommunities = (user: UserOut) => user.role === "supadmin";
const showTeams = (user: UserOut) =>
  user.role === "admin" || user.role === "supadmin";
// T2 — super admins manage assessments in the full editor; admins and
// managers get a read-only view of the assessments that apply to teams
// in their visibility scope.
const showAssessments = (user: UserOut) =>
  user.role === "supadmin" ||
  user.role === "admin" ||
  user.role === "manager";
const showSearch = showAll;
const showManageUsers = (user: UserOut) =>
  user.role === "admin" || user.role === "supadmin";
// PET-641 — admins and supadmins curate the Ask-a-Question preset
// questions; managers consume them but never see the editor. This nav
// gate is cosmetic — the server enforces the same rule on PUT
// /settings/questions (403 for managers).
const showManageQuestions = (user: UserOut) =>
  user.role === "admin" || user.role === "supadmin";
// Wave 10 (port-plan 24) — admins and supadmins generate practice sales
// scenarios; managers never see the entry. This nav gate is cosmetic —
// every scenario endpoint is role-gated server-side (require_role).
const showGenerateScenarios = (user: UserOut) =>
  user.role === "admin" || user.role === "supadmin";

// `satisfies Record<SidebarNavKey, NavItem>` is the heart of the build-time
// guarantee: TypeScript will fail compilation if a SidebarNavKey is missing
// from this map, OR if any entry omits a required field on NavItem
// (including `usage`, `usage.summary`, and `usage.howToUse`). Adding a new
// sidebar destination therefore *forces* the developer to write its docs
// here, in the same place, before `pnpm build` will pass.
// Explicit object type keeps this data registry from being mistaken for a
// React component by Fast Refresh lint (the old outer TS assertion hid it).
export const NAV_REGISTRY: Readonly<Record<SidebarNavKey, NavItem>> = {
  intelligence: {
    key: "intelligence",
    label: "Teams",
    section: "top",
    icon: <BarChart3 className="h-4 w-4" aria-hidden="true" />,
    isVisible: showAll,
    usage: {
      summary:
        "Compare call volume, sentiment and assessment performance across the teams you can access.",
      howToUse: [
        "Choose a period such as Weekly or Monthly, or a Custom date range. Calendar uses calendar periods; Rolling uses a trailing period. Use comparison with the prior period where available (not All time).",
        "Choose a Team, then optionally an Agent. Managers see only their assigned teams; All teams combines only teams within your access.",
        "Select Registered calls, Viable calls or Quality calls to focus the funnel; select a sentiment card to focus sentiment. Select again to remove the filter. Open team sentiment segments for an agent breakdown.",
        "Switch call volume between Daily, Hourly and Weekday to inspect activity patterns.",
        "Expand Assessment overview to see Team performance organised by assessment. Open an assessment card and its team breakdown, then select a measure or label to inspect matching agents and calls. Open an agent profile or call for the evidence; drilldowns carry the selected date scope.",
      ],
    },
  },
  agents: {
    key: "agents",
    label: "Agent",
    section: "top",
    icon: <UserRound className="h-4 w-4" aria-hidden="true" />,
    isVisible: showAll,
    usage: {
      summary:
        "A directory of every agent you can see, with their headline call stats.",
      howToUse: [
        "Use the directory's search, team and period controls to find an agent in your access scope, then open their profile.",
        "Expand Compare Agents. Use Compare Agents in Team to choose a team, or Compare Agents to choose individuals, and inspect their performance side by side within the selected period.",
        "In the profile, choose a period, Calendar or Rolling where available, or a Custom range. Compare calls analysed, average duration and sentiment with the team averages.",
        "Select sentiment cards or Dialpad labels and tags to filter Calls. Multiple selected tags must all match; select a tag again to remove it.",
        "Expand Assessment performance and select measures or labels to inspect the matching calls. If nothing was graded, widen the date range rather than treating the empty view as a zero score.",
        "Open Training Tips for coaching based on the selected agent, range and labels. Managers get the default call scope; admins and super admins choose Calls analysed and explicitly generate the report. Use Generate Training Module for a follow-on module and Copy to retain tips or module text; these are not saved permanent reports.",
        "Open a call to inspect its transcript, recap and available assessment results, including the evidence behind detected labels. Use the back navigation to return to the originating list.",
      ],
    },
  },
  communities: {
    key: "communities",
    label: "Manage Communities",
    section: "bottom",
    icon: <Shield className="h-4 w-4" aria-hidden="true" />,
    isVisible: showCommunities,
    usage: {
      summary:
        "Manage the top-level communities (brands) that group teams and users.",
      howToUse: [
        "Super admins only: create a community before creating its teams or assigning its admins.",
        "Edit its name, Draft or Active status, brand selection, extra agents, force-excluded agents and assigned admins. Draft communities are hidden from admins.",
        "Set the Default community where appropriate and save your changes.",
        "Use Delete to remove a community, not archive it. Read the confirmation and its consequences before proceeding.",
      ],
    },
  },
  teams: {
    key: "teams",
    label: "Manage Teams",
    section: "bottom",
    icon: <Layers className="h-4 w-4" aria-hidden="true" />,
    isVisible: showTeams,
    usage: {
      summary:
        "Create and edit the teams of agents that sit inside each community.",
      howToUse: [
        "Admins and super admins can create and edit teams within their community scope. Pick the Community and Name, choose Agents and Managers assigned, then Save. Review duplicate-agent warnings.",
        "Select the assessments the team should use in the team editor, or open its assessment assignment control from the table. Save assignments before choosing a newly assigned scenario success label.",
        "For an existing team, expand Scenarios. Admins can inspect these settings read-only; only super admins can change Enable scenarios, Success measure, Team scenario instructions and difficulty criteria.",
        "Success measure must be a detection label from an assessment assigned to this team. It defines the business outcome used to split calls; do not assume every team measures a sale.",
        "Set Beginner, Intermediate and Advanced criteria for FAIL, PARTIAL, PASS and BONUS. Coaching milestones such as a callback are not automatically confirmed business outcomes. Team instructions cannot override privacy, evidence grounding, statistics or the export format.",
        "Configuration does not mean a team is enabled. Check its Scenarios status; disabled teams remain Coming soon in Generate Scenarios. Save any authorised configuration changes explicitly.",
      ],
    },
  },
  search: {
    key: "search",
    label: "Search",
    section: "top",
    icon: <Search className="h-4 w-4" aria-hidden="true" />,
    isVisible: showSearch,
    usage: {
      summary:
        "Search by customer phone number or agent name to see every recorded call.",
      howToUse: [
        "Choose Search By Number or Search By Agent. Enter at least six characters for phone suggestions or two for agent suggestions, then select a match or press Search.",
        "A new search starts with Weekly / Rolling, with transcripts and assessed calls. Widen the period or change transcript and assessed filters if calls appear missing. Filter by agent, team and direction as needed; the agent is fixed when searching by agent.",
        "Open a call from the results to inspect its transcript and assessments. Switching search tabs retains each tab's latest search and filters while this page remains open.",
        "Open Ask a Question to query the filtered call selection. Choose a preset question bubble to fill the text, edit it if needed, and send, or enter your own question. The dialog explains any cap on recent calls used.",
        "Use Copy in Ask a Question to copy the Q&A and call references. Keep a copy before closing the conversation if you need it later.",
      ],
    },
  },
  // Wave 5 · T1 — Roundtable: multi-agent comparison Q&A. Visible to
  // all three roles (every role's answers are grounded only in calls
  // the caller is already allowed to see — the server composes the
  // visibility predicate on every read).
  roundtable: {
    key: "roundtable",
    label: "Roundtable",
    section: "top",
    icon: <MessagesSquare className="h-4 w-4" aria-hidden="true" />,
    isVisible: showAll,
    usage: {
      summary:
        "Put several agents side by side and ask the same questions across their calls.",
      howToUse: [
        "Search for agents or a team to add its members to the panel, up to 12 agents. Remove individual panel members when needed; only agents you can access are offered.",
        "Choose a Custom range or use the default last seven days, up to 100 calls per agent. Direction starts at Outbound and transcript-only is on; adjust these before asking.",
        "In Ask the panel, managers can ask only the preset question bubbles, which send immediately. Admins and super admins can also type their own question and use Ask; their preset bubbles fill the text for editing.",
        "Read each agent's answer, calls used and actual covered dates alongside the comparison takeaway. Follow call references to inspect evidence; ask further questions to append results.",
        "Switch Thread or Matrix and use Copy Report to copy the active report layout. Clear Results removes the Q&A and draft question but keeps the current panel and filters.",
        "There is no saved Roundtable report: reloading resets the panel, filters and results. Copy anything you need to keep before reloading or leaving the page.",
      ],
    },
  },
  // Wave 10 (port-plan 24) — Generate Scenarios: admin/supadmin
  // scenario-generation entry point, rendered directly below Roundtable
  // at the end of the top section (before Dialpad).
  "generate-scenarios": {
    key: "generate-scenarios",
    label: "Generate Scenarios",
    section: "top",
    icon: <Sparkles className="h-4 w-4" aria-hidden="true" />,
    isVisible: showGenerateScenarios,
    usage: {
      summary:
        "Analyse team call evidence and build downloadable roleplay scenarios (admins and super admins only).",
      howToUse: [
        "Choose an enabled Team you can access. Disabled teams are marked Coming soon; ask a super admin about availability in Manage Teams. Saved configuration alone does not enable generation.",
        "Pick Window end date (NZ time, inclusive) plus 6, 12, 18 or 24 days before it: 7, 13, 19 or 25 days total. The default is seven days. Future end dates are not allowed.",
        "AI analysis uses at most 200 unique calls, balanced across date sections, people, configured outcomes and resistance types. This is an engineering starting point, not a guaranteed model capacity; a fixed token budget also limits excerpts. Sampling coverage separates viable, evidence-eligible and sampled calls and flags sparse or unrepresented groups. Population statistics come from assessment evidence, never sample conversion percentages.",
        "Review Viable transcripts per person. Eligible calls must belong to the selected team and community, be viable and have usable transcripts in the window. Total transcripts are split by the team's configured success label detected or not detected, not by a universal Sales definition.",
        "Whole team uses the team’s eligible pool. Choose Single select and click one or more people to analyse their transcripts together.",
        "Click Identify scenarios and wait for analysis to finish. Both outcome groups are analysed. Read the resistance distributions, narrative comparison and suggested scenario tiles. Empty or insufficient evidence produces an error, not invented results.",
        "Expand a suggested tile to inspect its details. Generate & download builds its full JSON file; Building… shows progress, Try again retries a failed build, and Download scenario downloads a ready file. Several tiles can build at once.",
        "Choose Bulk to generate a team package, individual packages, or both. Pick one to five scenarios for the team and one to five per selected person. Each individual is analysed separately. The package contains only scenarios supported by the evidence.",
        "Return to the same team and window to see batch progress. Download files individually or as a ZIP. Retry failed work to keep successful files, or use Stop remaining work to keep finished files and prevent further generation.",
        "Download ready files before the expiry date shown beside the downloads.",
        "Source Team is automatically included in the JSON as originating-team metadata, independent of which people supplied transcripts.",
        "The latest completed analysis, period, people selection and tile files are restored on return. Changing the range or people hides reports for other selections. Generation reuses the saved sample without resampling. Older reports are marked as legacy seven-day full analyses.",
        "Clear scenarios asks you to confirm the named team's saved analysis reports and generated files will be permanently deleted. Cancel keeps them unchanged; Delete reports and files removes them, but not source calls or transcripts. Download any files you want to keep before confirming.",
      ],
    },
  },
  assessments: {
    key: "assessments",
    label: "Assessments",
    section: "bottom",
    icon: <ClipboardCheck className="h-4 w-4" aria-hidden="true" />,
    isVisible: showAssessments,
    usage: {
      summary:
        "Browse the assessments used to score your calls and see what each one measures.",
      howToUse: [
        "Open an assessment to read its measures, detection labels and criteria. Admins and managers have a read-only view of assessments assigned to teams in their scope.",
        "Super admins can create and edit assessments, including their name, description, measures and detection instructions, then Save as Draft or Save as offered. Review any impact confirmation when changing measures. Delete assessment removes the definition and team assignments while preserving existing call results.",
        "Assign saved assessments to teams in Manage Teams. Review graded results from Teams, agent profiles or individual calls; editing an assessment is different from running it over calls.",
      ],
    },
  },
  dialpad: {
    key: "dialpad",
    label: "Dialpad",
    section: "bottom",
    icon: <Phone className="h-4 w-4" aria-hidden="true" />,
    isVisible: showDialpad,
    usage: {
      summary:
        "Sync Dialpad calls, inspect coverage and investigate missing data (admins and super admins).",
      howToUse: [
        "Use From last sync to fetch from the last logged call, capped at 72 hours. Review Will fetch first. Users and groups refresh at the start, followed by calls, transcripts/recaps and assessments; watch In progress and Sync History for errors.",
        "Use Schedules to review automatic syncs. A super admin can add a schedule or use Disable to pause the quick-sync and schedule controls.",
        "Inspect Data Coverage, refresh its counts and use the missing-transcript retry control when appropriate. A missing transcript may still be unavailable from Dialpad.",
        "In Call Sync Reconciliation choose a range and Generate Report to inspect missing or suspect days. Admins can generate and inspect reports; only super admins can Check against Dialpad…, choose check severities, Correct confirmed gaps or use Check and rectify. Cancel stops an in-flight check while retaining partial results.",
        "Read the confirmation before clearing sync history. The action also deletes call data.",
      ],
    },
  },
  data: {
    key: "data",
    label: "Data",
    section: "bottom",
    icon: <Database className="h-4 w-4" aria-hidden="true" />,
    isVisible: showData,
    usage: {
      summary:
        "Super-admin data tools — manually sync calls, manage Dialpad users, run assessments and clear results.",
      howToUse: [
        "Super admins only: in Advanced Sync Call Data choose a range, review existing coverage and Will fetch, then Sync. Normal sync skips data already covered.",
        "Use Phase 1 for call metadata, Phase 2 for transcripts and recaps, or Phase 3 for assessments. Users and groups refresh as a prerequisite. Monitor the resulting work in Dialpad.",
        "Re-sync (force) reprocesses covered data and can wipe and rebuild calls in the selected window. Check the range carefully before pressing Sync or a Phase button.",
        "Sync and inspect Dialpad users in the users section. Use the assessment runner to select a range, run assessments and monitor progress.",
        "Use the delete-results controls only when you intend to remove assessment results. Review the selected scope and confirmation; this is separate from clearing scenario files or sync history.",
      ],
    },
  },
  users: {
    key: "users",
    label: "Manage Users",
    section: "bottom",
    icon: <Users className="h-4 w-4" aria-hidden="true" />,
    isVisible: showManageUsers,
    usage: {
      summary:
        "Add, edit and remove the people who can sign in to ConvoAI.",
      howToUse: [
        "Admins and super admins use Create User with first name, last name, work email and role. Users sign in with Microsoft.",
        "Use Edit to update permitted profile details. Only super admins can change another user's role; admins can manage admin and manager accounts in scope. Managers can edit only their own account.",
        "Review Communities / Teams on each row. Assign community admins in Manage Communities and team managers in Manage Teams rather than editing memberships here.",
        "Use Remove to delete an eligible account after confirmation, not deactivate it. You cannot remove yourself; managers cannot remove users.",
      ],
    },
  },
  "manage-questions": {
    key: "manage-questions",
    label: "Manage Questions",
    section: "bottom",
    icon: <FileQuestion className="h-4 w-4" aria-hidden="true" />,
    isVisible: showManageQuestions,
    usage: {
      summary:
        "Edit reusable preset questions for Search and Roundtable (admins and super admins only).",
      howToUse: [
        "Use Add question to create a question or statement, up to 50 entries. Edit the text directly or remove an unwanted row.",
        "Drag the reorder handle, or use its keyboard move controls, to order questions. Search and Roundtable group questions by colour while preserving their order within each group.",
        "Choose a colour and target one or more teams, All teams or no teams. Team targeting controls who receives the preset bubbles; unassigned questions are not offered as team presets.",
        "Filter by colour or team to find rows without changing them. Select rows or Select all to bulk-edit colours and team assignments; Select all includes nonblank rows even when filters hide them.",
        "Press Save to persist the edited list, ordering and assignments; blank rows are omitted. Unsaved changes do not update the preset bubbles. Reset discards local edits.",
        "In Search, presets fill the editable question. In Roundtable, managers send presets directly and cannot type free text; admins and super admins can edit the filled text before asking.",
      ],
    },
  },
  "how-to-use": {
    key: "how-to-use",
    label: "How to Use",
    section: "bottom",
    icon: <BookOpen className="h-4 w-4" aria-hidden="true" />,
    isVisible: showAll,
    usage: {
      summary:
        "This guide — a plain-English description of every page you can open from the sidebar.",
      howToUse: [
        "Search for a page or feature using titles, summaries or instruction text, then expand its section.",
        "On desktop, choose a Table of Contents link to open that section. On mobile, scroll the sections and use their accordion buttons. Multiple sections can be opened individually.",
        "Items you don't have permission to see are hidden automatically.",
      ],
    },
  },
};

// Stable display order. Top section: Teams (intelligence), Agent,
// Search, Roundtable, Generate Scenarios. Bottom section: Dialpad,
// Data, Assessments, Manage Users, Manage Questions, Manage Teams,
// Manage Communities, How to Use. Sidebar items are rendered in this
// order, filtered by visibility.
export const NAV_ITEMS: readonly NavItem[] = [
  NAV_REGISTRY.intelligence,
  NAV_REGISTRY.agents,
  NAV_REGISTRY.search,
  NAV_REGISTRY.roundtable,
  NAV_REGISTRY["generate-scenarios"],
  NAV_REGISTRY.dialpad,
  NAV_REGISTRY.data,
  NAV_REGISTRY.assessments,
  NAV_REGISTRY.users,
  NAV_REGISTRY["manage-questions"],
  NAV_REGISTRY.teams,
  NAV_REGISTRY.communities,
  NAV_REGISTRY["how-to-use"],
];

export function visibleNavItems(
  user: UserOut,
  section?: NavSection,
): NavItem[] {
  return NAV_ITEMS.filter(
    (i) => i.isVisible(user) && (section ? i.section === section : true),
  );
}
